import { supabase } from '@/lib/supabase';
import type {
  EventAttachment,
  EventType,
  Medication,
  TimelineEvent,
  VitalSigns,
} from '@/types/db';

export interface TimelineEventWithExtras extends TimelineEvent {
  medications: Medication[];
  attachments: EventAttachment[];
}

export async function listTimelineEvents(
  profileId: string,
): Promise<TimelineEventWithExtras[]> {
  const { data, error } = await supabase
    .from('timeline_events')
    .select(
      `
        *,
        medications(*),
        event_attachments(*)
      `,
    )
    .eq('profile_id', profileId)
    .eq('is_draft', false)
    .order('event_date', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });

  if (error) throw error;

  type Row = TimelineEvent & {
    medications?: Medication[];
    event_attachments?: EventAttachment[];
  };
  return ((data ?? []) as Row[]).map((row) => ({
    ...row,
    medications: row.medications ?? [],
    attachments: row.event_attachments ?? [],
  }));
}

export async function getTimelineEvent(
  profileId: string,
  eventId: string,
): Promise<TimelineEventWithExtras | null> {
  const { data, error } = await supabase
    .from('timeline_events')
    .select(`*, medications(*), event_attachments(*)`)
    .eq('id', eventId)
    .eq('profile_id', profileId)
    .eq('is_draft', false)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  if (!data) return null;

  type Row = TimelineEvent & {
    medications?: Medication[];
    event_attachments?: EventAttachment[];
  };
  const row = data as Row;
  return {
    ...row,
    medications: row.medications ?? [],
    attachments: row.event_attachments ?? [],
  };
}

export async function listAllMedications(
  profileId: string,
): Promise<Medication[]> {
  const { data, error } = await supabase
    .from('medications')
    .select('*, timeline_events!inner(profile_id, event_date)')
    .eq('timeline_events.profile_id', profileId)
    .order('is_stopped', { ascending: true })
    .order('start_date', { ascending: false, nullsFirst: false });
  if (error) throw error;
  return (data ?? []) as Medication[];
}

export async function listActiveMedications(
  profileId: string,
): Promise<Medication[]> {
  // Join via timeline_events to scope by profile and fetch event_date as
  // fallback start_date for rows saved before start_date was populated.
  const { data, error } = await supabase
    .from('medications')
    .select('*, timeline_events!inner(profile_id, event_date)')
    .eq('timeline_events.profile_id', profileId)
    .eq('is_stopped', false);
  if (error) throw error;

  const today = new Date().toISOString().slice(0, 10);
  type MedRow = Medication & {
    timeline_events: { profile_id: string; event_date: string | null } | { profile_id: string; event_date: string | null }[] | null;
  };
  return (data ?? []).filter((row: MedRow) => {
    const te = row.timeline_events;
    const eventDate = Array.isArray(te) ? te[0]?.event_date : te?.event_date;
    return isMedActive(row, today, eventDate ?? null);
  }) as Medication[];
}

export function isMedActive(
  m: { end_date?: string | null; start_date?: string | null; duration_days?: number | null },
  today: string,
  eventDate: string | null = null,
): boolean {
  if (m.end_date) return m.end_date >= today;
  // Use explicit start_date, falling back to the prescription's event_date.
  const effectiveStart = m.start_date ?? eventDate;
  if (effectiveStart && m.duration_days != null) {
    const start = new Date(effectiveStart);
    if (!Number.isNaN(start.getTime())) {
      const finished = new Date(start);
      finished.setDate(finished.getDate() + m.duration_days);
      return finished.toISOString().slice(0, 10) >= today;
    }
  }
  // Start date present but no duration limit → chronic medication, treat as active.
  if (effectiveStart) return true;
  // No date anchors at all → assume chronic/ongoing.
  return true;
}

export interface VitalsHistoryEntry {
  id: string;
  event_date: string | null;
  event_type: EventType;
  vital_signs: VitalSigns;
}

function isNonEmptyVitals(vs: unknown): vs is VitalSigns {
  if (!vs || typeof vs !== 'object') return false;
  return Object.values(vs as Record<string, unknown>).some(
    (v) => typeof v === 'number',
  );
}

export async function listVitalsHistory(
  profileId: string,
): Promise<VitalsHistoryEntry[]> {
  const { data, error } = await supabase
    .from('timeline_events')
    .select('id, event_date, event_type, vital_signs')
    .eq('profile_id', profileId)
    .eq('is_draft', false)
    .order('event_date', { ascending: true, nullsFirst: true });
  if (error) throw error;

  return ((data ?? []) as VitalsHistoryEntry[]).filter((row) =>
    isNonEmptyVitals(row.vital_signs),
  );
}

export interface LatestVitalReading {
  id: string;
  event_date: string | null;
  vital_signs: VitalSigns;
}

export async function getLatestVital(
  profileId: string,
): Promise<LatestVitalReading | null> {
  const { data, error } = await supabase
    .from('timeline_events')
    .select('id, event_date, vital_signs')
    .eq('profile_id', profileId)
    .eq('is_draft', false)
    .eq('event_type', 'vitals')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const row = data as LatestVitalReading;
  return isNonEmptyVitals(row.vital_signs) ? row : null;
}

export interface ProfileTimelineStats {
  latest: string | null;
  count: number;
}

export async function listTimelineStatsByProfile(): Promise<
  Record<string, ProfileTimelineStats>
> {
  const { data, error } = await supabase
    .from('timeline_events')
    .select('profile_id, event_date, created_at')
    .eq('is_draft', false);
  if (error) throw error;

  type Row = {
    profile_id: string;
    event_date: string | null;
    created_at: string;
  };
  const stats: Record<string, ProfileTimelineStats> = {};
  for (const row of (data ?? []) as Row[]) {
    const stamp = row.event_date ?? row.created_at.slice(0, 10);
    const prev = stats[row.profile_id] ?? { latest: null, count: 0 };
    prev.count += 1;
    if (!prev.latest || stamp > prev.latest) prev.latest = stamp;
    stats[row.profile_id] = prev;
  }
  return stats;
}

export async function listActiveMedCountsByProfile(): Promise<
  Record<string, number>
> {
  // Single batched query across every profile the caller can see (RLS filters it).
  // Only selects the minimum needed to compute the count + group key.
  const { data, error } = await supabase
    .from('medications')
    .select('end_date, start_date, duration_days, timeline_events!inner(profile_id, event_date)')
    .eq('is_stopped', false);
  if (error) throw error;

  const today = new Date().toISOString().slice(0, 10);
  const counts: Record<string, number> = {};
  type Row = {
    end_date: string | null;
    start_date: string | null;
    duration_days: number | null;
    timeline_events: { profile_id: string; event_date: string | null } | { profile_id: string; event_date: string | null }[] | null;
  };
  for (const row of (data ?? []) as Row[]) {
    const te = row.timeline_events;
    const eventDate = Array.isArray(te) ? te[0]?.event_date : te?.event_date;
    if (!isMedActive(row, today, eventDate ?? null)) continue;
    // supabase-js may surface the embedded parent as an array or an object
    // depending on the type inference — normalise both.
    const profileId = Array.isArray(te) ? te[0]?.profile_id : te?.profile_id;
    if (!profileId) continue;
    counts[profileId] = (counts[profileId] ?? 0) + 1;
  }
  return counts;
}
